import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar/Sidebar";
import { updatePilotsStatus, fetchPilotsAndVehicles } from "../Apis/AdminApis";
import { useNavigate } from "react-router-dom";
import "./UpdatePilotStatus.css";
import pavamanLogo from "../images/drone.png";
import PopupMessage from "../PopupMessage/PopupMessage";

const UpdatePilotStatus = () => {
  const navigate = useNavigate();
  const [pilots, setPilots] = useState([]);
  const [filteredPilots, setFilteredPilots] = useState([]);
  const [selectedPilots, setSelectedPilots] = useState([]); 
  const [selectedPilot, setSelectedPilot] = useState(null); 
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("info");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState(null); 
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    loadPilots();
  }, []);

  useEffect(() => {
    setSelectAll(false);
  }, [filteredPilots]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

 const loadPilots = async () => {
  try {
    setIsLoading(true);
    
    const response = await fetchPilotsAndVehicles({});
    if (response?.vehicles && Array.isArray(response.vehicles)) {
      const pilotsMap = new Map();
      
      response.vehicles.forEach(vehicle => {
        const pilotName = vehicle.initially_assigned_pilot_name;
        if (pilotName && !pilotsMap.has(pilotName)) {
          const pilotId = `pilot_${Date.now()}_${Math.random()}`;
          pilotsMap.set(pilotName, {
            id: pilotId,
            email: `${pilotName.toLowerCase().replace(/\s/g, '.')}@pilot.com`,
            name: pilotName,
            first_name: pilotName.split(' ')[0] || pilotName,
            last_name: pilotName.split(' ').slice(1).join(' ') || '',
            mobile_no: null,
            status: 1, 
            status_label: "Active Pilot",
            status_color: "#00B894",
            status_bg: "#d1fae5",
            status_icon: "✓",
            company_name: vehicle.admin_name,
            created_on: vehicle.created_at,
            vehicles: response.vehicles.filter(v => v.initially_assigned_pilot_name === pilotName),
            vehicle_count: response.vehicles.filter(v => v.initially_assigned_pilot_name === pilotName).length
          });
        }
      });
      
      const formattedPilots = Array.from(pilotsMap.values());
      
      setPilots(formattedPilots);
      setFilteredPilots(formattedPilots);
      setCurrentPage(1);
    } else {
      setPilots([]);
      setFilteredPilots([]);
    }
  } catch (err) {
    console.error("Failed to load pilots:", err);
    setPopupMessage("Failed to load pilots. Please try again.");
    setPopupType("error");
    setPopupOpen(true);
  } finally {
    setIsLoading(false);
  }
};

  const handleSelectPilot = (pilotId) => {
    setSelectedPilots(prev => {
      if (prev.includes(pilotId)) {
        return prev.filter(id => id !== pilotId);
      } else {
        return [...prev, pilotId];
      }
    });
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedPilots([]);
      setSelectAll(false);
    } else {
      const allIds = filteredPilots.map(pilot => pilot.id);
      setSelectedPilots(allIds);
      setSelectAll(true);
    }
  };

  const handleBulkAction = (action) => {
    if (selectedPilots.length === 0) {
      setPopupMessage("Please select at least one pilot");
      setPopupType("error");
      setPopupOpen(true);
      return;
    }
    
    const pilotsToUpdate = filteredPilots.filter(pilot => selectedPilots.includes(pilot.id));
    const canActivate = pilotsToUpdate.every(pilot => pilot.status === 0);
    const canSuspend = pilotsToUpdate.every(pilot => pilot.status === 1);
    
    if (action === 'activate' && !canActivate) {
      setPopupMessage("Only deactivated pilots can be activated");
      setPopupType("error");
      setPopupOpen(true);
      return;
    }
    
    if (action === 'suspend' && !canSuspend) {
      setPopupMessage("Only active pilots can be deactivateded");
      setPopupType("error");
      setPopupOpen(true);
      return;
    }
    
    setActionType(action);
    setShowConfirmModal(true);
  };

  const handleBulkUpdateStatus = async () => {
    if (selectedPilots.length === 0) return;

    try {
      setIsUpdating(true);
      const newStatus = actionType === 'activate' ? 1 : 0;
      const pilotsToUpdate = filteredPilots.filter(pilot => selectedPilots.includes(pilot.id));
      const results = await Promise.all(
        pilotsToUpdate.map(async (pilot) => {
          const response = await updatePilotsStatus({
            pilot_email: pilot.email,
            status: newStatus
          });
          return { pilot, response };
        })
      );
      const errors = results.filter(r => r.response?.error);
      if (errors.length > 0) {
        setPopupMessage(`Failed to update ${errors.length} pilot(s). Please try again.`);
        setPopupType("error");
        setPopupOpen(true);
      } else {
        setPopupMessage(`Successfully ${actionType === 'activate' ? 'activated' : 'suspended'} ${results.length} pilot(s)`);
        setPopupType("success");
        setPopupOpen(true);
        await loadPilots();
        setSelectedPilots([]);
        setSelectAll(false);
      }
    } catch (err) {
      console.error("Failed to update pilot status:", err);
      setPopupMessage("Failed to update pilot status. Please try again.");
      setPopupType("error");
      setPopupOpen(true);
    } finally {
      setIsUpdating(false);
      setShowConfirmModal(false);
      setActionType(null);
    }
  };

  const handleSingleUpdateStatus = async () => {
    if (!selectedPilot) return;

    try {
      setIsUpdating(true);
      const newStatus = actionType === 'activate' ? 1 : 0;
      
      const response = await updatePilotsStatus({
        pilot_email: selectedPilot.email,
        status: newStatus
      });

      if (response.error) {
        setPopupMessage(response.error);
        setPopupType("error");
        setPopupOpen(true);
      } else if (response.message) {
        setPopupMessage(response.message);
        setPopupType("success");
        setPopupOpen(true);
        await loadPilots();
      }
    } catch (err) {
      console.error("Failed to update pilot status:", err);
      setPopupMessage("Failed to update pilot status. Please try again.");
      setPopupType("error");
      setPopupOpen(true);
    } finally {
      setIsUpdating(false);
      setShowConfirmModal(false);
      setSelectedPilot(null);
      setActionType(null);
    }
  };

  const openSingleConfirmModal = (pilot, action) => {
    setSelectedPilot(pilot);
    setActionType(action);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setSelectedPilot(null);
    setActionType(null);
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    filterPilots(term, filterStatus);
  };

  const handleFilterChange = (status) => {
    setFilterStatus(status);
    filterPilots(searchTerm, status);
  };

  const filterPilots = (search, status) => {
    let filtered = [...pilots];
    
    if (search) {
      filtered = filtered.filter(pilot => 
        pilot.name?.toLowerCase().includes(search) ||
        pilot.email?.toLowerCase().includes(search) ||
        pilot.mobile_no?.includes(search)
      );
    }
    
    if (status !== 'all') {
      filtered = filtered.filter(pilot => 
        status === 'active' ? pilot.status === 1 : pilot.status === 0
      );
    }
    
    setFilteredPilots(filtered);
    setSelectedPilots([]);
    setSelectAll(false);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPilots = filteredPilots.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPilots.length / itemsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const formatMobile = (mobile) => {
    if (!mobile) return "N/A";
    return mobile.startsWith('+91') ? mobile : `+91 ${mobile}`;
  };

  const stats = {
    total: pilots.length,
    active: pilots.filter(p => p.status === 1).length,
    suspended: pilots.filter(p => p.status === 0).length
  };

  const getSelectedPilotsCount = () => {
    return selectedPilots.length;
  };

  return (
    <div className="dashboard-wrapper">
      <Sidebar />

      <PopupMessage
        isOpen={popupOpen}
        onClose={() => setPopupOpen(false)}
        message={popupMessage}
        type={popupType}
      />
      
      <div className="dashboard-content">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="dashboard-title">Pilot Status Management</h1>
            <p className="dashboard-subtitle">Activate or deactivate pilot accounts</p>
          </div>
        </div>

        {getSelectedPilotsCount() > 0 && (
          <div className="ups-pilot-status-bulk-actions">
            <span className="ups-pilot-status-selected-count">
              {getSelectedPilotsCount()} pilot(s) selected
            </span>
            <div className="ups-pilot-status-bulk-buttons">
              <button 
                className="ups-pilot-status-bulk-activate"
                onClick={() => handleBulkAction('activate')}
                disabled={isUpdating}
              >
                ✓ Bulk Activate
              </button>
              <button 
                className="ups-pilot-status-bulk-suspend"
                onClick={() => handleBulkAction('suspend')}
                disabled={isUpdating}
              >
                ✗ Bulk Deactivate
              </button>
              <button 
                className="ups-pilot-status-bulk-clear"
                onClick={() => {
                  setSelectedPilots([]);
                  setSelectAll(false);
                }}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        <div className="ups-pilot-status-filter-section">
          <div className="ups-pilot-status-search-wrapper">
            <svg className="ups-pilot-status-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
            </svg>
            <input
              type="text"
              placeholder="Search by name, email or mobile..."
              value={searchTerm}
              onChange={handleSearch}
              className="ups-pilot-status-search-input"
            />
            {searchTerm && (
              <button className="ups-pilot-status-clear-search" onClick={() => handleSearch({ target: { value: "" } })}>
                ✕
              </button>
            )}
          </div>

          <div className="ups-pilot-status-filters">
            <button
              className={`ups-pilot-status-filter-btn ${filterStatus === "all" ? "active" : ""}`}
              onClick={() => handleFilterChange("all")}
            >
              All ({stats.total})
            </button>
            <button
              className={`ups-pilot-status-filter-btn ${filterStatus === "active" ? "active" : ""}`}
              onClick={() => handleFilterChange("active")}
            >
              Active ({stats.active})
            </button>
            <button
              className={`ups-pilot-status-filter-btn ${filterStatus === "suspended" ? "active" : ""}`}
              onClick={() => handleFilterChange("suspended")}
            >
              Deactivate ({stats.suspended})
            </button>
          </div>
        </div>

        <div className="ups-pilot-status-results-info">
          <span className="ups-pilot-status-results-count">
            Showing {filteredPilots.length} of {pilots.length} pilots
          </span>
        </div>

        {isLoading ? (
          <div className="ups-pilot-status-loading-container">
            <img src={pavamanLogo} alt="Loading" className="ups-pilot-status-spinning-logo" />
            <p className="ups-pilot-status-loading-text">Loading pilots...</p>
          </div>
        ) : filteredPilots.length === 0 ? (
          <div className="ups-pilot-status-no-results">
            <div className="ups-pilot-status-no-results-icon">👨‍✈️</div>
            <h3>No pilots found</h3>
            <p>Try adjusting your search or filter criteria</p>
          </div>
        ) : (
          <>
            <div className="tr-table-container">
              <table className="tr-table">
                <thead className="tr-thead">
                  <tr>
                    <th style={{ width: "40px" }}>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="ups-pilot-status-checkbox"
                        disabled={filteredPilots.length === 0}
                      />
                    </th>
                    <th>S.No</th>
                    <th>Pilot</th>
                    <th>Contact</th>
                    <th>Vehicles</th>
                    <th>Status</th>
                    <th>Registered On</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPilots.map((pilot, index) => (
                    <tr key={pilot.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedPilots.includes(pilot.id)}
                          onChange={() => handleSelectPilot(pilot.id)}
                          className="ups-pilot-status-checkbox"
                        />
                      </td>
                      <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td>
                        <div className="ups-pilot-status-pilot-info">
                          <div 
                            className="ups-pilot-status-pilot-avatar" 
                            style={{ background: pilot.status_color }}
                          >
                            {pilot.name?.charAt(0) || "P"}
                          </div>
                          <div>
                            <div className="ups-pilot-status-pilot-name">{pilot.name}</div>
                            <div className="ups-pilot-status-pilot-email">{pilot.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="ups-pilot-status-contact">
                          <div className="ups-pilot-status-mobile">{formatMobile(pilot.mobile_no)}</div>
                        </div>
                      </td>
                      <td>
                        <span className="ups-pilot-status-vehicle-count">
                          {pilot.vehicle_count} Vehicle{pilot.vehicle_count !== 1 ? 's' : ''}
                        </span>
                      </td>
                      <td>
                        <span 
                          className="ups-pilot-status-badge"
                          style={{ 
                            backgroundColor: pilot.status_bg, 
                            color: pilot.status_color 
                          }}
                        >
                          {pilot.status_icon} {pilot.status_label}
                        </span>
                      </td>
                      <td>
                        <div className="ups-pilot-status-date">{formatDate(pilot.created_on)}</div>
                      </td>
                      <td>
                        <div className="ups-pilot-status-actions">
                          {pilot.status === 0 && (
                            <button 
                              className="ups-pilot-status-btn-activate"
                              onClick={() => openSingleConfirmModal(pilot, 'activate')}
                              disabled={isUpdating}
                            >
                              ✓ Activate Pilot
                            </button>
                          )}
                          {pilot.status === 1 && (
                            <button 
                              className="ups-pilot-status-btn-suspend"
                              onClick={() => openSingleConfirmModal(pilot, 'suspend')}
                              disabled={isUpdating}
                            >
                              ✗ Deactivate Pilot
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredPilots.length > itemsPerPage && (
              <div className="pagination-container">
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>
                  
                  <div className="page-numbers">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = i + 1;
                      } else if (currentPage <= 3) {
                        pageNumber = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNumber = totalPages - 4 + i;
                      } else {
                        pageNumber = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNumber}
                          className={`page-btn ${currentPage === pageNumber ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showConfirmModal && (
        <div className="ups-pilot-status-modal-overlay" onClick={closeConfirmModal}>
          <div className="ups-pilot-status-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ups-pilot-status-modal-header">
              <h2>
                {actionType === 'activate' ? 'Activate' : 'Suspend'} 
                {selectedPilots.length > 0 ? ` ${selectedPilots.length} Pilot(s)` : ' Pilot'}
              </h2>
              <button className="ups-pilot-status-modal-close" onClick={closeConfirmModal}>×</button>
            </div>
            <div className="ups-pilot-status-modal-body">
              <div className="ups-pilot-status-confirm-content">
                <div className={`ups-pilot-status-confirm-icon ups-pilot-status-confirm-${actionType}`}>
                  {actionType === 'activate' ? '✓' : '✗'}
                </div>
                <p className="ups-pilot-status-confirm-text">
                  Are you sure you want to {actionType}
                  {selectedPilots.length > 0 ? (
                    ` ${selectedPilots.length} pilot(s)?`
                  ) : (
                    <>
                      {" "}the pilot <strong>{selectedPilot?.name}</strong>?
                    </>
                  )}
                </p>
                <p className="ups-pilot-status-confirm-subtext">
                  {actionType === 'activate' 
                    ? 'The pilot(s) will be able to access the platform and operate drones.'
                    : 'The pilot(s) will be suspended and will not be able to access the platform.'}
                </p>
                <div className="ups-pilot-status-confirm-buttons">
                  <button 
                    className="ups-pilot-status-confirm-cancel"
                    onClick={closeConfirmModal}
                    disabled={isUpdating}
                  >
                    Cancel
                  </button>
                  <button 
                    className={`ups-pilot-status-confirm-${actionType}`}
                    onClick={selectedPilot ? handleSingleUpdateStatus : handleBulkUpdateStatus}
                    disabled={isUpdating}
                  >
                    {isUpdating ? (
                      <>
                        <span className="ups-pilot-status-spinner-small"></span>
                        Processing...
                      </>
                    ) : (
                      actionType === 'activate' ? 'Yes, Activate' : 'Yes, Suspend'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpdatePilotStatus;